import {
  danger,
  routeToUrl,
  success,
  useCustomForm,
  useEffect,
  useLocation,
  useMemo,
  useParams,
  useUpload,
} from "@";
import {
  useCertificateImageDesign,
  useHasCustomerSuggestedCampaign,
} from "./index";
import { fillForm } from "../lib/helpers";
import { defaultValues, schema } from "../lib/validation";
import {
  useAdahiDataQuery,
  useProjectDataQuery,
  useProjectQuery,
} from "../api/queries/index";
import { useSaveProjectMutation } from "../api/mutations/index";
import type {
  BankTableRow,
  CardDesignFormValues,
  Category,
  CustomerCampaign,
  GiftProject,
  SelectOption,
} from "../types";

/* eslint-disable @typescript-eslint/no-explicit-any */

interface LocationState {
  customerCampaign?: {
    row: CustomerCampaign;
  };
}

interface UseProjectFormReturn {
  // Form
  register: any;
  watch: any;
  setValue: any;
  getValues: any;
  control: any;
  errors: any;
  handleSubmit: (
    fn: () => Promise<void>,
  ) => (e?: React.BaseSyntheticEvent) => Promise<void>;
  isSubmitting: boolean;
  isSubmitSuccessful: boolean;

  // Route params
  edit: string | undefined;
  show: string | undefined;
  isViewMode: boolean;

  // Data
  banks: BankTableRow[];
  categories: Category[];
  giftProjects: GiftProject[];

  // Options
  categoriesOptions: SelectOption[];
  adahiProductsOptions: SelectOption[];
  adahiRitualOptions: SelectOption[];
  accountsOptions: BankTableRow[];

  // Error & Links
  error: Error | null;
  links: { name: string; link: string }[];

  // Customer Campaign
  customerCampaign: CustomerCampaign | null;

  // Handlers
  submit: () => Promise<void>;
  refetchAll: () => void;
  handleUpload: (file: File, field: string) => Promise<void>;
  handleUploadCertificate: (file: File, preview: string) => Promise<void>;
  handleSelect: (params: { selectedRows: { id: number }[] }) => void;
  checkOnSelectedAccounts: (row: { id: number }) => boolean;

  // Certificate Design
  saveCertificateImageDesign: (designs: CardDesignFormValues[]) => void;
  showCertificateImageDesigner: () => void;
  isShowCertificateImageDesigner: boolean;
  certificateImageDesign: CardDesignFormValues[];
  closeCertificateImageDesigner: () => void;
}

export const useProjectForm = (): UseProjectFormReturn => {
  const location = useLocation();
  const { edit, show } = useParams();
  const customerCampaign =
    (location.state as LocationState)?.customerCampaign?.row || null;

  // Use React Query hooks
  const {
    data: projectData,
    error: projectDataError,
    refetch: refetchProjectData,
  } = useProjectDataQuery();

  const {
    data: project,
    error: projectError,
    refetch: refetchProject,
  } = useProjectQuery(edit || show);

  const { data: adahiData, refetch: refetchAdahi } = useAdahiDataQuery();

  const saveProjectMutation = useSaveProjectMutation();

  // Refetch all data function
  const refetchAll = async () => {
    await refetchProjectData();
    if (edit || show) {
      await refetchProject();
    }
    await refetchAdahi();
  };

  // Extract data from queries
  const banks = projectData?.banks?.data || [];
  const categories = projectData?.categories || [];
  const giftProjects = projectData?.giftProjects || [];
  const adahiProducts = adahiData?.adahiProducts || [];
  const adahiRitual = adahiData?.adahiRitual || [];

  // Error state
  const error = projectDataError || projectError;
  const links = [
    { name: "المشاريع", link: "/projects" },
    { name: edit ? "تعديل" : show ? "عرض" : "اضافة", link: "" },
  ];

  const {
    reset,
    watch,
    errors,
    control,
    setValue,
    register,
    getValues,
    handleSubmit,
    isSubmitting,
    isSubmitSuccessful,
  } = useCustomForm(defaultValues, schema);

  // Initialize gift card design handling
  const {
    saveCertificateImageDesign,
    showCertificateImageDesigner,
    isShowCertificateImageDesigner,
    certificateImageDesign,
    setIsShowCertificateImageDesigner,
  } = useCertificateImageDesign(getValues, setValue);

  const { handleUpload } = useUpload(setValue);
  const handleUploadCertificate = async (file: File, preview: string) => {
    setValue("certificate", file);
    setValue("certificateView", preview);
    showCertificateImageDesigner();
  };

  // Close Certificate card designer
  const closeCertificateImageDesigner = () => {
    document.body.style.overflow = "auto";
    setIsShowCertificateImageDesigner(false);
  };

  const { setFromFields } = useHasCustomerSuggestedCampaign(
    getValues,
    setValue,
    customerCampaign,
  );

  // Effect to populate form when project data is loaded
  useEffect(() => {
    if (project) {
      reset(fillForm(project));
    }
  }, [project, reset]);

  // Effect to set customer campaign fields
  useEffect(() => {
    if (customerCampaign?.id) {
      setValue("customerSuggestedCampaignId", customerCampaign.id);
      setFromFields();
    }
  }, [customerCampaign, setValue, setFromFields]);

  const checkOnSelectedAccounts = (row: { id: number }) =>
    (getValues("bankAccountsIdsArray") as number[])?.includes(row.id) ||
    (getValues("bankAccountsIds") as number[])?.includes(row.id);

  const submit = async () => {
    try {
      await saveProjectMutation.mutateAsync(getValues() as never);
      await success("تم الحفظ بنجاح");
      if (customerCampaign?.id) {
        routeToUrl("/customers-campaigns");
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.log("🚀 ~ submit ~ error: ", err);
    }
  };

  const handleSelect = ({
    selectedRows,
  }: {
    selectedRows: { id: number }[];
  }) => {
    const ids = selectedRows.map((el) => el?.id);
    setValue("bankAccountsIds", ids);
  };

  // Memoized options
  const categoriesOptions = useMemo(
    () =>
      categories?.map((option: Category) => ({
        value: option.id,
        label: option.name,
      })) || [],
    [categories],
  );

  const adahiProductsOptions = useMemo(
    () =>
      adahiProducts?.map((option) => ({
        value: option.id,
        label: option.name,
      })) || [],
    [adahiProducts],
  );

  const adahiRitualOptions = useMemo(
    () =>
      adahiRitual?.map((option) => ({
        value: option.id,
        label: `${option?.name} : ${option?.brief} `,
      })) || [],
    [adahiRitual],
  );

  const accountsOptions = useMemo(
    () =>
      banks.flatMap((bank) =>
        bank.bankAccounts?.map((account) => ({
          ...account,
          bankName: bank.name,
          bankImageFullUrl: bank.imageFullUrl,
        })),
      ) || [],
    [banks],
  );

  useEffect(() => {
    if (Object.keys(errors).length > 0) danger("يوجد خطأ في البيانات المدخلة");
  }, [errors]);

  const isViewMode = !!show;

  return {
    // Form
    register,
    watch,
    setValue,
    getValues,
    control,
    errors,
    handleSubmit,
    isSubmitting,
    isSubmitSuccessful,

    // Route params
    edit,
    show,
    isViewMode,

    // Data
    banks: accountsOptions as BankTableRow[],
    categories,
    giftProjects,

    // Options
    categoriesOptions,
    adahiProductsOptions,
    adahiRitualOptions,
    accountsOptions: accountsOptions as BankTableRow[],

    // Error & Links
    error: error as Error | null,
    links,

    // Customer Campaign
    customerCampaign,

    // Handlers
    submit,
    refetchAll,
    handleUpload,
    handleUploadCertificate,
    handleSelect,
    checkOnSelectedAccounts,

    // Certificate Design
    saveCertificateImageDesign,
    showCertificateImageDesigner,
    isShowCertificateImageDesigner,
    certificateImageDesign,
    closeCertificateImageDesigner,
  };
};
